import { Axios } from 'axios';

import { logger } from '@/lib/logger';
import { getBackendUrl } from '@/lib/runtime-config';

import { BaseHttpClient, HttpClientConfig } from './BaseHttpClient';

export interface ServerApiConfig extends HttpClientConfig {
  /** Use the secure API backend URL (recommended) */
  useSecureApi?: boolean;
}

/**
 * ServerApiService — base server-side HTTP client used by server services.
 *
 * It wraps BaseHttpClient and provides server-specific error logging.
 */
export class ServerApiService extends BaseHttpClient {
  private useSecureApi: boolean = true;

  constructor(config?: ServerApiConfig) {
    const { useSecureApi = true, ...httpConfig } = config || {};

    const defaultConfig: HttpClientConfig = {
      baseURL: useSecureApi ? `${getBackendUrl()}` : 'http://api.example.com/api',
      timeout: 30000,
      enableLogging: true,
      enableInterceptors: true,
      ...httpConfig,
    };

    super(defaultConfig);
    this.useSecureApi = useSecureApi;
  }

  /**
   * Override setupInterceptors to add server-specific error handling
   */
  protected setupInterceptors(): void {
    super.setupInterceptors();

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        logger.error({
          message: 'Server API Request Error',
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          error: error.response?.data || error.message,
        });
        return Promise.reject(error);
      },
    );
  }

  /**
   * Server-side generic request method.
   *
   * This exposes an opinionated wrapper that returns the unwrapped `data` payload,
   * which is useful for server code that expects the backend's response body directly.
   */
  public async serverRequest<T = any>(method: string, url: string, data?: any, options?: any): Promise<T> {
    try {
      const apiResp = await this.request<{ data: T }>({
        method,
        url,
        data,
        ...options,
      });
      return apiResp.data;
    } catch (error) {
      logger.error({
        message: 'Server API Request Error',
        error,
      });
      throw error;
    }
  }
}

/**
 * Adapter that uses V3HttpClient under the hood (OAuth + token refresh).
 *
 * This class is useful when you need all the BaseHttpClient utilities but want
 * the V3 OAuth token handling and retry behavior.
 */
export class ServerApiAdapter extends ServerApiService {
  constructor(config?: Partial<ServerApiConfig>) {
    super({
      baseURL: getBackendUrl(),
      enableInterceptors: true,
      ...config,
    });

    logger.debug({
      msg: 'ServerApiAdapter initialized',
      baseURL: getBackendUrl(),
    });
  }
}

export { ServerApiAdapter as SecureServerApiService };
